/**
 * Admin V3 — Contacts — Search / filter bar
 *
 * Layout: [Status filter] [Search input] [Result counter]
 *
 * Status filter: 4 values (new / read / replied / archived) — server-side
 * (?status= query param, validated by the backend PATCH/GET taxonomy).
 * Search input is client-side over the loaded page (name + email + message).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { t } from '../../i18n';

const ALL_SENTINEL = '__all__';
const STATUS_VALUES = ['new', 'read', 'replied', 'archived'];

const ContactsListSearchBar = ({
  status,
  onStatusChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const statusSelectValue = status || ALL_SENTINEL;
  const handleStatus = (v) => onStatusChange(v === ALL_SENTINEL ? '' : v);
  const resultsCountText = t('contacts.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Status filter (server-side) */}
      <Select value={statusSelectValue} onValueChange={handleStatus}>
        <SelectTrigger
          className="h-10 w-[180px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-contacts-status-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('contacts.filter.statusAll', locale)}</SelectItem>
          {STATUS_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{t(`contacts.status.${v}`, locale)}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Client-side search */}
      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('contacts.search.placeholder', locale)}
          data-testid="adminv3-contacts-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-contacts-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Result counter */}
      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-contacts-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default ContactsListSearchBar;
